import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
  getDigitalChannelDataSelector,
  getMonitoringError,
  getMonitoringLoading,
  getSensordataSelector,
} from '../../../../../redux/monitoring/selectors';
import {
  fetchMetrics,
  updateFilter,
} from '../../../../../redux/monitoring/thunk';

import {
  CommanderChannelsFilterLogicalObject,
  CommanderMonitoringFilterLogicalObject,
  getFilterLogicalObjectFromZipDeviceControlUnits,
} from '../util/filter.model';
import { filterAnalogChannels } from '../util/filterAnalogChannels';
import { filterDigitalChannels } from '../util/filterDigitalChannels';
import { useDevicesControlUnits } from '../../../../../redux/controlUnit/hooks/useDevicesControlUnits';
import { STTimeLineChartData, TimeFrame } from '../monitoring.model';
import { filterExtrapolatedDigitalChannelData } from '../util/filterExtrapolatedDigitalChannelData';
import { checkDefaultChannels } from '../util/checkDefaultChannels';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { toBase64 } from '../../../../../shared/util/base64';
import { useCUsTranslate } from '../../../../../redux/CUTranslation/hook/useCUsTranslate';
import getPaletteColors, {
  getPaletteColorsByControlUnitsDevices,
} from '../util/getPaletteColors';
import { useTemperaturePreference } from '../../../../../service/temperaturePreference';
import { useGetDefaultChannels } from './useGetDefaultChannels';
import { BROKER_CONNECTION_EVENT_ID } from '../../../../../service/monitoring/util/mapEventsDigitalToSTCharTimeLine';

export interface UseMetricsProps {
  initialTimeFrameStart?: Moment;
  initialTimeFrameEnd?: Moment;
  deviceId?: string;
  commanderId?: string;
  groupId?: string;
  defaultAnalogFilter?: CommanderMonitoringFilterLogicalObject;
  defaultDigitalFilter?: CommanderMonitoringFilterLogicalObject;
  defaultCommanderFilter?: CommanderChannelsFilterLogicalObject;
}

export const useMetrics = (props: UseMetricsProps) => {
  const {
    initialTimeFrameStart = moment().subtract(6, 'hours'),
    initialTimeFrameEnd = moment(),
    deviceId,
    commanderId,
    groupId,
    defaultAnalogFilter,
    defaultDigitalFilter,
    defaultCommanderFilter,
  } = props;
  const navProps = {
    deviceId: toBase64(deviceId ?? ''),
    commanderId: toBase64(commanderId ?? ''),
    groupId,
  };

  const showLevelView = 'DEVICE';
  const dispatch = useAppDispatch();
  const loadingMetrics = useAppSelector(getMonitoringLoading);
  const errorMetrics = useAppSelector(getMonitoringError);
  const {
    devicesControlUnits,
    loading: loadingDevicesControlUnits,
    refetch: refetchDevicesControlUnits,
  } = useDevicesControlUnits(navProps);

  useEffect(() => {
    refetchDevicesControlUnits();
  }, [refetchDevicesControlUnits]);
  const [, loadingCUsTranslate] = useCUsTranslate(navProps);
  const [readyToFetch, setReadyToFetch] = useState(false);
  const loading = loadingMetrics !== false || !readyToFetch;

  // Filter controls
  const [timeFrame, setTimeFrame] = useState<TimeFrame>({
    start: initialTimeFrameStart,
    end: initialTimeFrameEnd,
  });

  const { data: defaultChannels, isLoading: isLoadingDefaultChannels } =
    useGetDefaultChannels(commanderId ?? '');

  const defaultFilterObjectChannels = useMemo(
    () => {
      if (defaultAnalogFilter) return defaultAnalogFilter;
      // Hydrate stored value
      return checkDefaultChannels(
        getFilterLogicalObjectFromZipDeviceControlUnits(
          devicesControlUnits,
          'channels',
          false
        ),
        defaultChannels
      );
    },
    // I don't want update this every time value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devicesControlUnits, defaultAnalogFilter, defaultChannels]
  );

  const defaultFilterObjectEvents = useMemo(
    () => {
      if (defaultDigitalFilter) return defaultDigitalFilter;

      // Hydrate stored value
      return checkDefaultChannels(
        getFilterLogicalObjectFromZipDeviceControlUnits(
          devicesControlUnits,
          'events',
          false
        ),
        defaultChannels
      );
    },
    // I don't want update this every time value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devicesControlUnits, defaultDigitalFilter, defaultChannels]
  );

  const digitalControls = useState<CommanderMonitoringFilterLogicalObject>(
    defaultFilterObjectEvents
  );
  const analogControls = useState<CommanderMonitoringFilterLogicalObject>(
    defaultFilterObjectChannels
  );
  const [digitalFilterObjectEvents, setDigitalFilterObject] = digitalControls;
  const [analogFilterObjectEvents, setAnalogFilterObject] = analogControls;
  const defaultCommanderFilterObject = useMemo(
    () => {
      if (defaultCommanderFilter) return defaultCommanderFilter;
      // By default only request the broker connection channel so the digital
      // channels can be blanked out while the device was disconnected.
      return devicesControlUnits.reduce((acc, deviceControlUnit) => {
        acc[deviceControlUnit.commanderId] = [BROKER_CONNECTION_EVENT_ID];
        return acc;
      }, {} as CommanderChannelsFilterLogicalObject);
    },
    // I don't want update this every time value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devicesControlUnits, defaultCommanderFilter]
  );
  const commanderControls = useState<CommanderChannelsFilterLogicalObject>(
    defaultCommanderFilterObject
  );
  const [commanderFilterObjectEvents, setCommanderFilterObject] =
    commanderControls;
  const externalSetDigitalFilterObject: typeof setDigitalFilterObject = (
    value
  ) => {
    setDigitalFilterObject(value);
    dispatch(
      updateFilter({
        ...navProps,
        type: 'events',
        value: value as CommanderMonitoringFilterLogicalObject,
      })
    );
  };
  const externalSetAnalogFilterObject: typeof setAnalogFilterObject = (
    value
  ) => {
    setAnalogFilterObject(value);
    dispatch(
      updateFilter({
        ...navProps,
        type: 'channels',
        value: value as CommanderMonitoringFilterLogicalObject,
      })
    );
  };

  // getting data

  const sensordata = useAppSelector((state) =>
    getSensordataSelector(state, navProps)
  );

  const digitalChannels = useAppSelector((state) =>
    getDigitalChannelDataSelector(state, navProps)
  );

  const sensordataFiltered = useMemo(() => {
    if (loading || isLoadingDefaultChannels) return [];
    return filterAnalogChannels(sensordata, analogFilterObjectEvents);
  }, [sensordata, analogFilterObjectEvents, loading, isLoadingDefaultChannels]);

  const digitalChannelsFiltered = useMemo(() => {
    if (loading || isLoadingDefaultChannels) return {} as STTimeLineChartData;
    return filterExtrapolatedDigitalChannelData(
      filterDigitalChannels(digitalChannels, digitalFilterObjectEvents),
      timeFrame.start.unix(),
      timeFrame.end.unix()
    );
  }, [
    digitalChannels,
    digitalFilterObjectEvents,
    loading,
    isLoadingDefaultChannels,
    timeFrame.end,
    timeFrame.start,
  ]);

  if (!(loading || loadingCUsTranslate || loadingDevicesControlUnits)) {
    // not loading
  }

  // generate colors
  const paletteColors = useMemo(() => {
    const paletteColorsControlUnits = getPaletteColorsByControlUnitsDevices(
      digitalFilterObjectEvents,
      analogFilterObjectEvents
    );
    return getPaletteColors(
      paletteColorsControlUnits,
      digitalChannelsFiltered,
      sensordataFiltered
    );
  }, [
    analogFilterObjectEvents,
    digitalFilterObjectEvents,
    digitalChannelsFiltered,
    sensordataFiltered,
  ]);

  // Fetch
  useEffect(() => {
    if (
      !(
        loadingDevicesControlUnits ||
        loadingCUsTranslate ||
        isLoadingDefaultChannels
      ) &&
      readyToFetch !== true
    ) {
      setDigitalFilterObject(defaultFilterObjectEvents);
      setAnalogFilterObject(defaultFilterObjectChannels);
      setCommanderFilterObject(defaultCommanderFilterObject);
      setReadyToFetch(true);
    }
  }, [
    defaultFilterObjectChannels,
    defaultFilterObjectEvents,
    defaultCommanderFilterObject,
    loadingCUsTranslate,
    loadingDevicesControlUnits,
    isLoadingDefaultChannels,
    readyToFetch,
    setAnalogFilterObject,
    setDigitalFilterObject,
    setCommanderFilterObject,
    analogFilterObjectEvents,
    digitalFilterObjectEvents,
  ]);
  const temperaturePreference = useTemperaturePreference();
  useEffect(() => {
    if (readyToFetch && !loadingCUsTranslate) {
      dispatch(
        fetchMetrics({
          devicesControlUnits,
          start: timeFrame.start.valueOf(),
          end: timeFrame.end.valueOf(),
          analogFilterObjectEvents,
          digitalFilterObjectEvents,
          commanderFilterObjectEvents,
          showLevelOption: showLevelView,
          temperaturePreference,
        })
      );
    }
    // ignore devicesControlUnits as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timeFrame,
    dispatch,
    readyToFetch,
    loadingCUsTranslate,
    analogFilterObjectEvents,
    digitalFilterObjectEvents,
    commanderFilterObjectEvents,
    temperaturePreference,
  ]);

  return {
    timeControls: { timeFrame, setTimeFrame },
    digitalControls: [
      digitalFilterObjectEvents,
      externalSetDigitalFilterObject,
    ] as typeof digitalControls,
    analogControls: [
      analogFilterObjectEvents,
      externalSetAnalogFilterObject,
    ] as typeof analogControls,
    commanderControls: [
      commanderFilterObjectEvents,
      setCommanderFilterObject,
    ] as typeof commanderControls,
    metrics: {
      sensordata: sensordataFiltered,
      digitalChannels: digitalChannelsFiltered,
    },
    paletteColors,
    loading:
      loading ||
      loadingCUsTranslate ||
      loadingDevicesControlUnits ||
      isLoadingDefaultChannels,
    error: errorMetrics,
  } as const;
};
